/**
 * Shared "server-side search + filter chips + saved filters" panel.
 *
 * Used by Orders, Users, Audit Logs, and Error Reports admin tabs to give
 * a consistent power-user search experience that scales to 10k+ rows.
 *
 * Props:
 *  - scope: "orders" | "users" | "audit" | "errors"
 *  - value: current filter state object { q, ...filter fields }
 *  - onChange: (newState) => void  // called whenever any field changes
 *  - filters: [{ key, label, type: "select" | "chips" | "days", options?, placeholder? }]
 *  - searchPlaceholder: free-text search placeholder
 *  - total?: total result count (for "Showing X of Y" UX)
 *  - resultCount?: number of items currently visible
 */
import { useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Bookmark, BookmarkPlus, Trash2, Search, X, ChevronDown, Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

export default function AdminSearchPanel({
  scope,
  value,
  onChange,
  filters = [],
  searchPlaceholder = "Search…",
  total,
  resultCount,
}) {
  const [saved, setSaved] = useState([]);
  const [globalPinnedCount, setGlobalPinnedCount] = useState(0);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showFiltersOpen, setShowFiltersOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  const loadSaved = async () => {
    try {
      const [scopeRes, pinnedRes] = await Promise.all([
        API.get(`/admin/saved-filters?scope=${encodeURIComponent(scope)}`),
        API.get("/admin/saved-filters/pinned").catch(() => ({ data: { pinned: [] } })),
      ]);
      setSaved(scopeRes.data.filters || []);
      setGlobalPinnedCount((pinnedRes.data.pinned || []).length);
    } catch (e) {
      // Non-fatal — saved filters are a power-user feature.
      // eslint-disable-next-line no-console
      console.warn("Failed to load saved filters:", e);
    }
  };
  useEffect(() => { loadSaved(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scope]);

  // When the user clicks a pinned filter on the Overview dashboard, the
  // widget stashes the payload in sessionStorage. Pick it up on mount so the
  // tab opens already filtered. One-shot — consumed immediately.
  useEffect(() => {
    try {
      const key = `pinned_filter_apply:${scope}`;
      const raw = sessionStorage.getItem(key);
      if (raw) {
        sessionStorage.removeItem(key);
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          onChange({ ...parsed });
        }
      }
    } catch { /* non-fatal */ }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [scope]);

  const pinnedCount = useMemo(() => saved.filter((f) => f.pinned).length, [saved]);
  // For the disabled state, use the GLOBAL pinned count (across all scopes)
  // since backend enforces max-3 globally — not per-scope.
  const pinDisabledGlobally = globalPinnedCount >= 3;

  const handlePin = async (filter) => {
    try {
      const url = filter.pinned ? `/admin/saved-filters/${filter.id}/unpin` : `/admin/saved-filters/${filter.id}/pin`;
      await API.post(url);
      toast.success(filter.pinned ? "Unpinned from dashboard" : "Pinned to dashboard");
      loadSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update pin");
    }
  };

  const activeCount = useMemo(() => {
    return Object.entries(value || {}).filter(([k, v]) => v !== "" && v !== null && v !== undefined && k !== "q").length;
  }, [value]);

  const handleSave = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaveLoading(true);
    try {
      await API.post("/admin/saved-filters", { name, scope, payload: value });
      toast.success(`Filter "${name}" saved`);
      setShowSaveDialog(false);
      setNewName("");
      loadSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleLoad = (filter) => {
    onChange({ ...filter.payload });
    toast.success(`Loaded "${filter.name}"`);
  };

  const handleDelete = async (filter) => {
    if (!(await brandedConfirm({ title: 'Delete?', description: `Delete saved filter "${filter.name}"?`, confirmText: 'Delete', cancelText: 'Cancel', tone: 'danger' }))) return;
    try {
      await API.delete(`/admin/saved-filters/${filter.id}`);
      toast.success("Deleted");
      loadSaved();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleClearAll = () => {
    const empty = { q: "" };
    filters.forEach((f) => { empty[f.key] = ""; });
    onChange(empty);
  };

  const update = (key, val) => onChange({ ...value, [key]: val });

  return (
    <div className="mb-4 space-y-3" data-testid={`admin-search-panel-${scope}`}>
      {/* Search bar + Saved-filters dropdown + actions */}
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={value.q || ""}
            onChange={(e) => update("q", e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 pr-9 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
            data-testid={`${scope}-search-input`}
          />
          {value.q && (
            <button
              type="button"
              onClick={() => update("q", "")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
              data-testid={`${scope}-search-clear`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {filters.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowFiltersOpen((v) => !v)}
            className="rounded-xl"
            data-testid={`${scope}-toggle-filters`}
          >
            <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${showFiltersOpen ? "rotate-180" : ""}`} />
            Filters
            {activeCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-purple-600 text-white text-[10px] font-bold">
                {activeCount}
              </span>
            )}
          </Button>
        )}

        {saved.length > 0 && (
          <Select
            value=""
            onValueChange={(id) => {
              const f = saved.find((x) => x.id === id);
              if (f) handleLoad(f);
            }}
          >
            <SelectTrigger className="w-[180px] rounded-xl bg-white dark:bg-slate-900" data-testid={`${scope}-saved-filter-select`}>
              <SelectValue placeholder={<><Bookmark className="w-3.5 h-3.5 inline mr-1" />Saved filters</>} />
            </SelectTrigger>
            <SelectContent>
              {saved.map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`${scope}-saved-filter-${f.id}`}>
                  <div className="flex items-center justify-between gap-2 w-full">
                    <span className="truncate">{f.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowSaveDialog(true)}
          className="rounded-xl"
          disabled={!value.q && activeCount === 0}
          title={!value.q && activeCount === 0 ? "Set a search or filter first" : "Save the current search + filters"}
          data-testid={`${scope}-save-filter-btn`}
        >
          <BookmarkPlus className="w-4 h-4 mr-1" /> Save
        </Button>

        {(value.q || activeCount > 0) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="rounded-xl text-slate-500"
            data-testid={`${scope}-clear-all`}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Filter chips (collapsible) */}
      {showFiltersOpen && filters.length > 0 && (
        <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filters.map((f) => {
            const v = value[f.key] ?? "";
            const tid = `${scope}-filter-${f.key}`;
            if (f.type === "select") {
              return (
                <div key={f.key}>
                  <Label className="text-xs text-slate-600 dark:text-slate-300">{f.label}</Label>
                  <Select value={v || "_any_"} onValueChange={(nv) => update(f.key, nv === "_any_" ? "" : nv)}>
                    <SelectTrigger className="mt-1 rounded-lg bg-white dark:bg-slate-900" data-testid={tid}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any_">{f.placeholder || "Any"}</SelectItem>
                      {(f.options || []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            if (f.type === "days") {
              return (
                <div key={f.key}>
                  <Label className="text-xs text-slate-600 dark:text-slate-300">{f.label}</Label>
                  <Select value={String(v || "_any_")} onValueChange={(nv) => update(f.key, nv === "_any_" ? "" : nv)}>
                    <SelectTrigger className="mt-1 rounded-lg bg-white dark:bg-slate-900" data-testid={tid}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any_">All time</SelectItem>
                      <SelectItem value="1">Last 24 hrs</SelectItem>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                      <SelectItem value="90">Last 90 days</SelectItem>
                      <SelectItem value="365">Last 12 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            // text input fallback
            return (
              <div key={f.key}>
                <Label className="text-xs text-slate-600 dark:text-slate-300">{f.label}</Label>
                <Input
                  value={v}
                  onChange={(e) => update(f.key, e.target.value)}
                  placeholder={f.placeholder || ""}
                  className="mt-1 rounded-lg bg-white dark:bg-slate-900"
                  data-testid={tid}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Saved filters management strip */}
      {saved.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <span className="mr-1">Saved:</span>
          {saved.map((f) => (
            <span
              key={f.id}
              className={`inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 ${
                f.pinned
                  ? "bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-800"
                  : "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
              }`}
            >
              {f.pinned && <Pin className="w-3 h-3 fill-amber-500 text-amber-500" />}
              <button
                type="button"
                onClick={() => handleLoad(f)}
                className="hover:underline"
                data-testid={`${scope}-saved-load-${f.id}`}
              >
                {f.name}
              </button>
              <button
                type="button"
                onClick={() => handlePin(f)}
                className={`ml-0.5 transition-colors ${f.pinned ? "text-amber-500 hover:text-amber-700" : "text-purple-300 hover:text-amber-500"}`}
                title={f.pinned ? "Unpin from dashboard" : (pinDisabledGlobally ? "3 pins max — unpin one first" : "Pin to dashboard")}
                aria-label={f.pinned ? `Unpin ${f.name}` : `Pin ${f.name}`}
                data-testid={`${scope}-saved-pin-${f.id}`}
                disabled={!f.pinned && pinDisabledGlobally}
              >
                {f.pinned ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
              </button>
              <button
                type="button"
                onClick={() => handleDelete(f)}
                className="text-purple-400 hover:text-rose-500 ml-0.5"
                aria-label={`Delete saved filter ${f.name}`}
                data-testid={`${scope}-saved-delete-${f.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          ))}
          {(pinnedCount > 0 || globalPinnedCount > 0) && (
            <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">
              {globalPinnedCount}/3 pinned to dashboard
            </span>
          )}
        </div>
      )}

      {/* Showing X of Y counter */}
      {(value.q || activeCount > 0) && typeof total === "number" && (
        <p className="text-xs text-slate-500 dark:text-slate-400 ml-1" data-testid={`${scope}-result-counter`}>
          Showing {resultCount ?? "—"} of {total}
        </p>
      )}

      {/* Save-filter modal */}
      <Dialog open={showSaveDialog} onOpenChange={(v) => { if (!v) { setShowSaveDialog(false); setNewName(""); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Save this search</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-sm">Name this filter</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={'e.g. "Open · FL · Last 7d"'}
              className="rounded-xl"
              autoFocus
              data-testid={`${scope}-save-filter-name`}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Only you can see this filter. It will appear in the &quot;Saved filters&quot; dropdown above.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSaveDialog(false); setNewName(""); }}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleSave}
              disabled={!newName.trim() || saveLoading}
              data-testid={`${scope}-save-filter-confirm`}
            >
              {saveLoading ? "Saving…" : "Save filter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
